import React, { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import VastAdPreview from './VastAdPreview';
import './AdPreview.css';

// Version constant - update this if needed
const APP_VERSION = '1.0.0';

// Direct Video File Component with caching support
function DirectVideoFile({ videoUrl }) {
  const videoRef = useRef(null);

  useEffect(() => {
    if (!videoUrl || !videoRef.current) return;

    const videoEl = videoRef.current;
    
    // Set up video element for autoplay and caching
    videoEl.muted = true;
    videoEl.loop = true;
    videoEl.autoplay = true;
    videoEl.playsInline = true;
    videoEl.controls = true;
    videoEl.preload = 'auto'; // Enable browser caching
    
    // Try to play
    const playPromise = videoEl.play();
    if (playPromise !== undefined) {
      playPromise.catch((error) => {
        console.log('[AdPreview] Autoplay prevented:', error);
      });
    }
    
    videoEl.addEventListener('loadeddata', () => {
      console.log('[AdPreview] Video loaded and cached:', videoUrl);
    });
    
    videoEl.addEventListener('error', (e) => {
      console.error('[AdPreview] Video loading error:', e);
    });
  }, [videoUrl]);

  return (
    <video
      ref={videoRef}
      src={videoUrl}
      autoPlay
      loop
      muted
      playsInline
      controls
      preload="auto"
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'contain'
      }}
    >
      Your browser does not support the video tag.
    </video>
  );
}

// Google Drive Video Component with autoplay support
function GoogleDriveVideo({ fileId }) {
  const videoRef = useRef(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [useIframe, setUseIframe] = useState(false);

  useEffect(() => {
    // Try multiple Google Drive video URL formats
    const videoUrls = [
      `https://drive.google.com/uc?export=download&id=${fileId}`,
      `https://drive.google.com/uc?export=view&id=${fileId}`,
      `https://drive.google.com/file/d/${fileId}/view?usp=sharing`
    ];

    // Try the first URL format
    setVideoUrl(videoUrls[0]);

    // Set up video element for autoplay
    if (videoRef.current) {
      const video = videoRef.current;
      video.muted = true;
      video.loop = true;
      video.autoplay = true;
      video.playsInline = true;
      video.controls = true;

      const handleError = () => {
        console.log('[AdPreview] Direct video URL failed, falling back to iframe');
        setUseIframe(true);
      };

      video.addEventListener('error', handleError);

      // Try to play
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.log('[AdPreview] Autoplay prevented:', error);
          // Still show the video, user can click to play
        });
      }

      return () => {
        video.removeEventListener('error', handleError);
      };
    }
  }, [fileId]);

  if (useIframe) {
    return (
      <iframe
        src={`https://drive.google.com/file/d/${fileId}/preview`}
        frameBorder="0"
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        title="Google Drive video player"
        style={{
          width: '100%',
          height: '100%',
          border: 'none'
        }}
      />
    );
  }

  return (
    <video
      ref={videoRef}
      src={videoUrl}
      autoPlay
      loop
      muted
      playsInline
      controls
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'contain'
      }}
    >
      Your browser does not support the video tag.
    </video>
  );
}

function AdPreview({ ad }) {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Extract all media images from ad object
  const mediaImages = [];
  if (ad?.media) {
    // Handle both array and object formats
    if (Array.isArray(ad.media)) {
      ad.media.forEach((item) => {
        if (item && item.image) {
          mediaImages.push({
            image: item.image,
            name: item.name || ''
          });
        }
      });
    } else {
      // Object format with numeric keys
      Object.keys(ad.media).forEach((key) => {
        const item = ad.media[key];
        if (item && item.image) {
          mediaImages.push({
            image: item.image,
            name: item.name || ''
          });
        }
      });
    }
  }

  // Calculate image display duration: 20 seconds divided by number of images
  const imageDisplayDuration = mediaImages.length > 0 ? (20 / mediaImages.length) * 1000 : 0; // Convert to milliseconds

  // Preload all images to prevent flickering
  useEffect(() => {
    if (mediaImages.length <= 1) return;
    
    mediaImages.forEach((imgData) => {
      const img = new Image();
      img.src = imgData.image;
    });
  }, [mediaImages]);

  // Rotate images automatically
  useEffect(() => {
    if (mediaImages.length <= 1) return; // No need to rotate if 0 or 1 image

    const timer = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % mediaImages.length);
    }, imageDisplayDuration);

    return () => clearInterval(timer);
  }, [mediaImages.length, imageDisplayDuration]);

  // Reset to first image when ad changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [ad?.id]);

  if (!ad) {
    return (
      <div className="ad-preview-empty">
        <div className="ad-preview-placeholder">
          <p>Select an ad from the list to preview</p>
        </div>
      </div>
    );
  }

  // Get current image and title
  const currentMediaImage = mediaImages.length > 0 ? mediaImages[currentImageIndex]?.image : null;
  const currentMediaTitle = mediaImages.length > 0 ? mediaImages[currentImageIndex]?.name : '';
  
  // Fallback to old format for backward compatibility
  const mediaImage = currentMediaImage || ad.media?.[0]?.image || ad.media?.['0']?.image;
  const mediaTitle = currentMediaTitle || ad.media?.[0]?.name || ad.media?.['0']?.name;
  const directVideoUrl = ad.hd_video || ad.video; // Prefer HD video if available
  const videoId = ad.vimeo; // Can be YouTube or Vimeo ID
  const description = ad.description;
  const staticQRCODE = ad.staticQRCODE;
  const titleQRCODE = ad.titleQRCODE;
  const version = APP_VERSION;
  const isPortrait = ad.portrait === true;
  const showFullscreenVideo = ad.show_fullscreen_video === true;
  const videoFullscreen = ad.video_fullscreen || '';
  const fullscreenBadgeImage = ad.fullscreen_badge_image || '';
  const vastTagUrl = typeof ad.vast_tag_url === 'string' ? ad.vast_tag_url.trim() : '';
  const showVastFullscreen = vastTagUrl.length > 0;
  const fullscreenLayout = showVastFullscreen || showFullscreenVideo;

  // Detect video type and extract ID
  const getVideoInfo = (videoId) => {
    if (!videoId) return { type: null, id: null };

    // Check if it's a Google Drive URL
    if (videoId.includes('drive.google.com')) {
      let fileId = null;
      // Extract file ID from various Google Drive URL formats
      const dMatch = videoId.match(/\/d\/([a-zA-Z0-9_-]+)/);
      const idMatch = videoId.match(/[?&]id=([a-zA-Z0-9_-]+)/);
      const fileMatch = videoId.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
      
      if (dMatch) {
        fileId = dMatch[1];
      } else if (fileMatch) {
        fileId = fileMatch[1];
      } else if (idMatch) {
        fileId = idMatch[1];
      }
      
      if (fileId) {
        return { type: 'googledrive', id: fileId, url: videoId };
      }
    }

    // Check if it's a YouTube URL
    const youtubeUrlPatterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /^([a-zA-Z0-9_-]{11})$/ // Just the ID (11 characters)
    ];

    for (const pattern of youtubeUrlPatterns) {
      const match = videoId.match(pattern);
      if (match) {
        return { type: 'youtube', id: match[1] || match[0] };
      }
    }

    // Check if it's a Vimeo URL
    const vimeoUrlPatterns = [
      /(?:vimeo\.com\/)(\d+)/,
      /^(\d+)$/ // Just the ID (numbers only)
    ];

    for (const pattern of vimeoUrlPatterns) {
      const match = videoId.match(pattern);
      if (match) {
        return { type: 'vimeo', id: match[1] || match[0] };
      }
    }

    // Default: assume it's a Vimeo ID if it's all numbers, otherwise YouTube
    if (/^\d+$/.test(videoId)) {
      return { type: 'vimeo', id: videoId };
    } else if (/^[a-zA-Z0-9_-]{11}$/.test(videoId)) {
      return { type: 'youtube', id: videoId };
    }

    // Fallback: try as Vimeo
    return { type: 'vimeo', id: videoId };
  };

  // Check if we have a direct video file URL (priority over streaming)
  const hasDirectVideo = directVideoUrl && typeof directVideoUrl === 'string' && directVideoUrl.trim() !== '';
  const videoInfo = hasDirectVideo ? { type: 'direct', url: directVideoUrl } : getVideoInfo(videoId);

  // Determine fullscreen video: use video_fullscreen if exists, otherwise fallback to video
  const fullscreenDirectVideoUrl = showFullscreenVideo 
    ? (videoFullscreen && videoFullscreen.trim() !== '' ? videoFullscreen : directVideoUrl)
    : null;
  const fullscreenVideoId = showFullscreenVideo 
    ? (videoFullscreen && videoFullscreen.trim() !== '' ? videoFullscreen : videoId)
    : null;
  const hasDirectFullscreenVideo = fullscreenDirectVideoUrl && typeof fullscreenDirectVideoUrl === 'string' && fullscreenDirectVideoUrl.trim() !== '';
  const fullscreenVideoInfo = showFullscreenVideo 
    ? (hasDirectFullscreenVideo ? { type: 'direct', url: fullscreenDirectVideoUrl } : getVideoInfo(fullscreenVideoId))
    : null;

  // Format time for display
  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour12: false,
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Image error handler with fallback URLs
  const handleImageError = (e) => {
    console.error(`[AdPreview] Image failed to load:`, mediaImage);
    console.error(`[AdPreview] Error details:`, e.target.error);
    
    // Try multiple fallback formats for Google Drive images
    if (mediaImage.includes('drive.google.com')) {
      // Extract file ID from various URL formats
      let fileId = null;
      const dMatch = mediaImage.match(/\/d\/([a-zA-Z0-9_-]+)/);
      const idMatch = mediaImage.match(/[?&]id=([a-zA-Z0-9_-]+)/);
      
      if (dMatch) {
        fileId = dMatch[1];
      } else if (idMatch) {
        fileId = idMatch[1];
      }
      
      if (fileId) {
        // Try fallback URLs in sequence
        const directUrl = `https://drive.google.com/uc?export=view&id=${fileId}`;
        const fallbackUrls = [
          `https://images.weserv.nl/?url=${encodeURIComponent(directUrl)}&w=1000`,
          `https://lh3.googleusercontent.com/d/${fileId}=w1000`,
          `https://drive.google.com/thumbnail?id=${fileId}&sz=w1000`,
          `https://corsproxy.io/?${encodeURIComponent(directUrl)}`,
          `https://drive.google.com/uc?export=download&id=${fileId}`,
          directUrl
        ];
        
        const currentAttempt = parseInt(e.target.getAttribute('data-fallback-attempt') || '0');
        
        if (currentAttempt < fallbackUrls.length) {
          const nextUrl = fallbackUrls[currentAttempt];
          console.log(`[AdPreview] Trying fallback URL ${currentAttempt + 1}/${fallbackUrls.length}:`, nextUrl);
          e.target.setAttribute('data-fallback-attempt', (currentAttempt + 1).toString());
          e.target.src = nextUrl;
        } else {
          console.error(`[AdPreview] All fallback URLs exhausted. File ID: ${fileId}`);
        }
      } else {
        console.error(`[AdPreview] Could not extract file ID for fallback from:`, mediaImage);
      }
    } else {
      console.error(`[AdPreview] Image URL is not a Google Drive link, cannot use fallback:`, mediaImage);
    }
  };

  const handleShowReport = () => {
    if (ad.id && ad.secret) {
      const reportUrl = `${window.location.origin}/ad/report?adId=${encodeURIComponent(ad.id)}&secret=${encodeURIComponent(ad.secret)}`;
      window.open(reportUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleShowMonthlyReports = () => {
    if (ad.id && ad.secret) {
      const url = `${window.location.origin}/ad/monthly-reports?adId=${encodeURIComponent(ad.id)}&secret=${encodeURIComponent(ad.secret)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Format phone number to international (Israel) for WhatsApp
  const formatPhone = (phone) => {
    if (!phone) return null;
    const digits = phone.replace(/\D/g, '');
    if (!digits) return null;
    if (digits.startsWith('972')) return digits;
    if (digits.startsWith('0')) return `972${digits.slice(1)}`;
    if (digits.length === 9) return `972${digits}`;
    return digits;
  };

  const handleShareReport = () => {
    const phone =
      ad.whatsapp_phone ||
      ad.business_phone ||
      ad.phone ||
      ad.phoneNumber ||
      '';
    const formatted = formatPhone(phone);
    if (!formatted) {
      alert('לא נמצא מספר טלפון תקין בפרסומת לשיתוף בוואטסאפ.');
      return;
    }

    const reportUrl = `${window.location.origin}/ad/report?adId=${encodeURIComponent(
      ad.id || ''
    )}&secret=${encodeURIComponent(ad.secret || '')}`;
    const message = encodeURIComponent(`קישור לדוח הפרסומת:\n${reportUrl}`);
    const waUrl = `https://wa.me/${formatted}?text=${message}`;
    window.open(waUrl, '_blank', 'noopener,noreferrer');
  };

  const handleSharePreviewVideo = () => {
    const phone =
      ad.whatsapp_phone ||
      ad.business_phone ||
      ad.phone ||
      ad.phoneNumber ||
      '';
    const formatted = formatPhone(phone);
    if (!formatted) {
      alert('לא נמצא מספר טלפון תקין בפרסומת לשיתוף בוואטסאפ.');
      return;
    }

    if (!ad.previewVideo) {
      alert('לא נמצא וידאו מקדים לפרסומת.');
      return;
    }

    const message = encodeURIComponent(`וידאו מקדים לפרסומת:\n${ad.previewVideo}`);
    const waUrl = `https://wa.me/${formatted}?text=${message}`;
    window.open(waUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="ad-preview">
      <div className="ad-preview-header">
        <div className="ad-preview-id-container">
          <span className="ad-preview-id">ID: {ad.id}</span>
          {ad.secret && (
            <span className="ad-preview-secret">Secret: {ad.secret}</span>
          )}
          {ad.id && ad.secret && (
            <button 
              className="ad-preview-show-report-btn"
              onClick={handleShowReport}
              title="View ad report"
            >
              הצג דוח
            </button>
          )}
          {ad.id && ad.secret && (
            <button
              className="ad-preview-share-report-btn"
              onClick={handleShareReport}
              title="Share report via WhatsApp"
            >
              שלח דוח
            </button>
          )}
          {ad.id && ad.secret && ad.generate_monthly_report === true && (
            <button
              type="button"
              className="ad-preview-show-report-btn"
              onClick={handleShowMonthlyReports}
              title="Monthly PDF reports"
            >
              דוחות חודשיים
            </button>
          )}
          {ad.id && !ad.secret && ad.previewVideo && (
            <button
              className="ad-preview-share-preview-video-btn"
              onClick={handleSharePreviewVideo}
              title="Share preview video via WhatsApp"
            >
              שלח וידאו מקדים
            </button>
          )}
        </div>
      </div>
      
      <div className={`ad-preview-content ${fullscreenLayout ? 'fullscreen-mode' : ''}`}>
        {/* Preview Screen Container - matches Android layout */}
        <div className={`ad-preview-screen ${fullscreenLayout ? 'fullscreen-mode' : ''}`}>
          {/* Top Banner - visible in all modes; dark style in fullscreen */}
          <div className={`ad-preview-top-banner ${fullscreenLayout ? 'fullscreen-mode' : ''}`}>
            {/* Strip Image - only when not fullscreen */}
            {!fullscreenLayout && (
              <div className="ad-preview-strip">
                <div className="ad-preview-strip-image"></div>
              </div>
            )}
            
            {/* Clock */}
            <div className="ad-preview-clock">
              {formatTime(currentTime)}
            </div>
            
            {/* Logo */}
            <div className="ad-preview-logo">
              <img src="/logo.png" alt="Logo" />
            </div>

            {/* Version */}
            <div className="ad-preview-version">
              1.2.8.tv:58
            </div>

            {/* Ad Description (addescription) */}
            {description && (
              <div className="ad-preview-top-description">
                <p>{description}</p>
              </div>
            )}
          </div>

          {/* Fullscreen: VAST (IMA) takes priority over MP4 fullscreen when vast_tag_url is set */}
          {showVastFullscreen ? (
            <div className="ad-preview-fullscreen-video ad-preview-vast-fullscreen">
              <VastAdPreview
                key={ad.id || 'vast'}
                vastTagUrl={vastTagUrl}
                embedMode="adPreviewFullscreen"
              />
              {fullscreenBadgeImage && fullscreenBadgeImage.trim() !== '' && (
                <div className="ad-preview-fullscreen-badge">
                  <img src={fullscreenBadgeImage} alt="Badge" />
                </div>
              )}
            </div>
          ) : showFullscreenVideo && fullscreenVideoInfo && (fullscreenVideoInfo.id || fullscreenVideoInfo.type === 'direct') ? (
            <div className="ad-preview-fullscreen-video">
              {fullscreenVideoInfo.type === 'direct' ? (
                <DirectVideoFile videoUrl={fullscreenVideoInfo.url} />
              ) : fullscreenVideoInfo.type === 'youtube' ? (
                <iframe
                  src={`https://www.youtube.com/embed/${fullscreenVideoInfo.id}?autoplay=1&loop=1&mute=1&controls=1&playlist=${fullscreenVideoInfo.id}`}
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  title="YouTube video player"
                ></iframe>
              ) : fullscreenVideoInfo.type === 'googledrive' ? (
                <GoogleDriveVideo fileId={fullscreenVideoInfo.id} />
              ) : (
                <iframe
                  src={`https://player.vimeo.com/video/${fullscreenVideoInfo.id}?autoplay=1&loop=1&muted=1&controls=1`}
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  title="Vimeo video player"
                ></iframe>
              )}
              {fullscreenBadgeImage && fullscreenBadgeImage.trim() !== '' && (
                <div className="ad-preview-fullscreen-badge">
                  <img src={fullscreenBadgeImage} alt="Badge" />
                </div>
              )}
            </div>
          ) : (
            /* Regular Video/Non-Video Layout (when fullscreen video is not enabled) */
            <>
              {/* Video Layout (when video exists) */}
              {(videoInfo.id || videoInfo.type === 'direct') ? (
            <div className="ad-preview-video-layout">
              {/* QR Code with Pulse Effects (left side in RTL) */}
              {staticQRCODE ? (
                <div className="ad-preview-qr-container">
                  <div className="ad-preview-qr-pulse-container">
                    <div className="ad-preview-qr-code">
                      <QRCodeSVG
                        value={staticQRCODE}
                        size={150}
                        level="H"
                        includeMargin={true}
                      />
                    </div>
                  </div>
                  {titleQRCODE && (
                    <div className="ad-preview-qr-title">
                      <p>{titleQRCODE}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="ad-preview-qr-container">
                  <div className="ad-preview-qr-placeholder">
                    <p>No QR code data available</p>
                  </div>
                </div>
              )}

              {/* TV Frame with Video (center) */}
              <div className={`ad-preview-video-frame-container ${isPortrait ? 'portrait' : ''}`}>
                <div className={`ad-preview-tv-frame ${isPortrait ? 'portrait' : ''}`}>
                  <div className="ad-preview-video-player">
                    {videoInfo.type === 'direct' ? (
                      <DirectVideoFile videoUrl={videoInfo.url} />
                    ) : videoInfo.type === 'youtube' ? (
                      <iframe
                        src={`https://www.youtube.com/embed/${videoInfo.id}?autoplay=1&loop=1&mute=1&controls=1&playlist=${videoInfo.id}`}
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture"
                        title="YouTube video player"
                      ></iframe>
                    ) : videoInfo.type === 'googledrive' ? (
                      <GoogleDriveVideo fileId={videoInfo.id} />
                    ) : (
                      <iframe
                        src={`https://player.vimeo.com/video/${videoInfo.id}?autoplay=1&loop=1&muted=1&controls=1`}
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture"
                        title="Vimeo video player"
                      ></iframe>
                    )}
                  </div>
                </div>
              </div>

              {/* Ad Image (right side in RTL) */}
              {mediaImage && (
                <div className="ad-preview-ad-image-container">
                  <div className="ad-preview-ad-image">
                    <img 
                      src={mediaImage} 
                      alt={mediaTitle || 'Ad Image'}
                      loading="lazy"
                      onLoad={() => {
                        console.log(`[AdPreview] Image loaded successfully:`, mediaImage);
                      }}
                      onError={handleImageError}
                    />
                  </div>
                  {mediaTitle && (
                    <div className="ad-preview-ad-name">
                      <p>{mediaTitle}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            /* Non-Video Layout (when no video) */
            <div className="ad-preview-non-video-layout">
              {/* QR Code Section */}
              <div className="ad-preview-non-video-qr-section">
                {staticQRCODE ? (
                  <>
                    <div className="ad-preview-qr-no-video-pulse-container">
                      <div className="ad-preview-qr-no-video">
                        <QRCodeSVG
                          value={staticQRCODE}
                          size={250}
                          level="H"
                          includeMargin={true}
                        />
                      </div>
                    </div>
                    {titleQRCODE && (
                      <div className="ad-preview-qr-no-video-title">
                        <p>{titleQRCODE}</p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="ad-preview-qr-no-video-placeholder">
                    <p>No QR code data available</p>
                  </div>
                )}
              </div>

              {/* Ad Image Section */}
              <div className="ad-preview-non-video-image-section">
                {mediaImage ? (
                  <>
                    <div className="ad-preview-ad-image-no-video">
                      <img 
                        src={mediaImage} 
                        alt={mediaTitle || 'Ad Image'}
                        loading="lazy"
                        onLoad={() => {
                          console.log(`[AdPreview] Image loaded successfully:`, mediaImage);
                        }}
                        onError={handleImageError}
                      />
                    </div>
                    {mediaTitle && (
                      <div className="ad-preview-ad-name-no-video">
                        <p>{mediaTitle}</p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="ad-preview-ad-image-placeholder">
                    <p>No image available</p>
                  </div>
                )}
              </div>
            </div>
          )}
            </>
          )}

          {/* Bottom Description/Title (description) - hidden in fullscreen mode */}
          {!fullscreenLayout && ad.title && (
            <div className="ad-preview-bottom-description">
              {ad.title.split('\n').map((line, index) => (
                <p key={index}>{line}</p>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdPreview;
